/**
 * `#uc2Drivers` — "What the change is made of" (client round 3 October 2026).
 *
 * The pattern chip paints a verdict; this block paints the readings the verdict is made
 * of, so a reader can see **why** a unit came out the way it did without opening the
 * methodology. One row per unit: farmland change over the period on the upper bar,
 * vegetated-cover change below it, the greening check as a third leg once the run serves
 * one, each signed against a zero baseline with the run's own stability band drawn as a
 * light strip behind them, and the rainfall change as a small tick across the row.
 *
 * **The colours are the engine's verdict, not a frontend threshold.** A bar is green when
 * the run's own `_ok` flag for that reading is true, neutral when it is false, and hatched
 * when it is `null` — the same three marks the map uses for the same three states.
 * Re-deriving them from the band here would put a green bar next to a grey unit the day a
 * curator moves the config.
 *
 * ## Countries are counted, not averaged
 *
 * A country row's numbers are the **mean** of its watersheds', and a mean cannot say how
 * many places sit behind it: four watersheds holding and seven shrinking can average to
 * the same figure as eleven doing neither. So at country level the colour comes from
 * `change_drivers.share_cropland_ok_pct` / `share_veg_ok_pct` — green at half the
 * watersheds or more, neutral below, hatched only where the run served no share at all —
 * and the share is said in words in the readout and in the focused sentence ("Farmland
 * held in 2 of 3 watersheds"). The greening leg reads `share_greening_ok_pct` the same
 * way, because a country carries no `greening_ok` of its own. The `_ok` flags are a
 * watershed's answer and are not read here.
 *
 * With a unit focused (`#intelFocus`, or a map click) the block narrows to that unit's own
 * bars and adds `#uc2DriversReading`: one plain sentence built from the very numbers
 * drawn, with the verbs chosen by sign and band (grew / held / shrank, wetter / near
 * average / drier). It says what the numbers are and stops there — nothing in it claims
 * one reading produced another.
 *
 * The focused block draws the greening leg too, as the same token (`DimensionBars`'
 * `token` row): the sentence under the chart names it, and a sentence describing a leg the
 * picture leaves out is the reader being told two different things at once.
 *
 * A run that carries no `change_drivers` yet prints one short note rather than an empty
 * frame.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelUc2ChangeDrivers, IntelUnitDetail, IntelUnitRow } from '../../lib/api';
import { ChartFrame } from '../../components/charts/ChartFrame';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { PairedBars, type PairedBarsLegendItem, type PairedBarsRow } from './charts/PairedBars';
import { driversReading, type Caption } from './uc2Captions';
import {
  DRIVER_NOT_COLOR,
  DRIVER_OK_COLOR,
  STABILITY_BAND_PCT,
  UC2_REST_COLOR,
} from './uc2Constants';

export interface Uc2DriversProps {
  /** The rows of the level the pane is on — one chart row each. */
  rows: IntelUnitRow[];
  /** `#intelLevel` — `'watershed'` or `'admin0'`, which decides counted or averaged. */
  level?: string;
  /** The focused unit's own row, or `undefined` with nothing focused. */
  focusedRow?: IntelUnitRow | undefined;
  /** `uc2/units/{id}`, the fallback for a focused unit whose row carries no drivers. */
  detail?: IntelUnitDetail | null;
  /** The run's own stability band, in percent. */
  band?: number;
  /** The focused unit's id, so its row is bold and outlined in the many-unit chart. */
  selectedId?: string | null;
}

/** Half the watersheds: at or above it a country's check reads as holding. */
export const COUNTRY_OK_SHARE_PCT = 50;

/** A finite number, or `null` — the one guard every reading below passes through. */
function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Whether a set of drivers carries anything at all to draw. */
function hasDrivers(drivers: IntelUc2ChangeDrivers | null | undefined): boolean {
  if (!drivers) return false;
  return (
    num(drivers.cropland_change_pct) != null ||
    num(drivers.veg_change_pct) != null ||
    num(drivers.rain_change_pct) != null ||
    num(drivers.share_cropland_ok_pct) != null ||
    num(drivers.share_veg_ok_pct) != null ||
    num(drivers.share_greening_ok_pct) != null ||
    drivers.greening_ok !== undefined
  );
}

/** The ink for one watershed reading: green when the run passed it, neutral when it did not. */
function inkOf(ok: boolean | null | undefined): string {
  return ok === true ? DRIVER_OK_COLOR : DRIVER_NOT_COLOR;
}

/** The ink for one country reading: green at half its watersheds or more. */
function shareInk(sharePct: number | null | undefined): string {
  const share = num(sharePct);
  return share != null && share >= COUNTRY_OK_SHARE_PCT ? DRIVER_OK_COLOR : DRIVER_NOT_COLOR;
}

export function Uc2Drivers({
  rows,
  level = 'watershed',
  focusedRow,
  detail,
  band = STABILITY_BAND_PCT,
  selectedId = null,
}: Uc2DriversProps) {
  const { t } = useTranslation();
  const title = t('intel.uc2.drivers.title');
  const caption = t('intel.uc2.caption.drivers');
  const countryLevel = level === 'admin0';

  /**
   * The focused unit's drivers: its row's when the row carries them, the detail's
   * otherwise. A unit focused at the other level has no row here, which is exactly the
   * case the detail covers.
   */
  const focusDrivers: IntelUc2ChangeDrivers | null = hasDrivers(focusedRow?.change_drivers)
    ? focusedRow!.change_drivers!
    : hasDrivers(detail?.change_drivers)
      ? detail!.change_drivers!
      : null;
  const focusName = focusedRow?.name ?? detail?.unit.name ?? '';
  const focused = Boolean(focusedRow ?? detail) && focusDrivers != null;
  /** A focused **country** is counted whichever way the level switch happens to sit. */
  const focusIsCountry = countryLevel || detail?.unit.level === 'admin0';

  const measured = rows.filter((row) => hasDrivers(row.change_drivers));

  /** One caption, translated. */
  const say = (c: Caption) => t(c.key, c.options ?? {});
  /**
   * The clauses as one list: "A", "A and B", "A, B and C". The two-item joiner is itself
   * a locale string, so the list is assembled here rather than in `uc2Captions`, which
   * deliberately holds no translator.
   */
  const joinClauses = (clauses: Caption[]): string => {
    const parts = clauses.map(say);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0]!;
    return t('intel.uc2.drivers.and', {
      a: parts.slice(0, -1).join(', '),
      b: parts[parts.length - 1]!,
    });
  };
  /** A clause standing alone as a sentence takes the capital the mid-sentence form lacks. */
  const openWith = (text: string) => (text ? text[0]!.toUpperCase() + text.slice(1) : '');

  /**
   * Whether a unit has a greening leg at all, and what that leg reads as.
   *
   * A watershed answers with `greening_ok`; a country answers with the share of its
   * watersheds that passed. `false` is a real answer and reads as one ("not rising"): it
   * is `null` alone that means nothing was judged, and only `null` hatches.
   */
  const greeningOf = (d: IntelUc2ChangeDrivers, asCountry: boolean) => {
    if (asCountry) {
      const share = num(d.share_greening_ok_pct);
      if (d.share_greening_ok_pct === undefined) return null;
      return {
        text:
          share == null
            ? t('intel.uc2.drivers.legend.unknown')
            : t('intel.uc2.drivers.sharePct', { pct: Math.round(share) }),
        color: shareInk(d.share_greening_ok_pct),
        hatched: share == null,
      };
    }
    if (d.greening_ok === undefined) return null;
    const ok = d.greening_ok;
    return {
      text:
        ok == null
          ? t('intel.uc2.drivers.legend.unknown')
          : t(`intel.uc2.drivers.token.${ok ? 'up' : 'notUp'}`),
      color: inkOf(ok),
      hatched: ok == null,
    };
  };

  const frame = (body: ReactNode) => (
    <div id="uc2Drivers" className="uc2-chart intel-card" data-testid="uc2-drivers">
      <h6 className="intel-card-title">{title}</h6>
      {body}
    </div>
  );

  if (!focused && !measured.length) {
    return frame(
      <p className="note" data-testid="uc2-drivers-empty">
        {t('intel.uc2.drivers.pending')}
      </p>,
    );
  }

  if (focused) {
    const drivers = focusDrivers!;
    const bars: DimRow[] = [
      {
        id: 'farmland',
        label: t('intel.uc2.drivers.row.farmland'),
        value: num(drivers.cropland_change_pct),
        unit: '%',
        color: focusIsCountry
          ? shareInk(drivers.share_cropland_ok_pct)
          : inkOf(drivers.cropland_ok),
      },
      {
        id: 'veg',
        label: t('intel.uc2.drivers.row.veg'),
        value: num(drivers.veg_change_pct),
        unit: '%',
        color: focusIsCountry ? shareInk(drivers.share_veg_ok_pct) : inkOf(drivers.veg_ok),
      },
      {
        id: 'rain',
        label: t('intel.uc2.drivers.row.rain'),
        value: num(drivers.rain_change_pct),
        unit: '%',
        color: UC2_REST_COLOR,
      },
    ];
    const focusGreening = greeningOf(drivers, Boolean(focusIsCountry));
    if (focusGreening) {
      // Between the two land readings and the rainfall context, which is where it sits in
      // the sentence and in the many-unit chart.
      bars.splice(2, 0, {
        id: 'greening',
        label: t('intel.uc2.drivers.row.greening'),
        value: null,
        token: true,
        valueText: focusGreening.text,
        color: focusGreening.color,
        hatched: focusGreening.hatched,
      });
    }
    /**
     * The country's own watershed count, so the sentence can say "2 of 3" rather than a
     * share. It lives on the country detail and nowhere else; without it the sentence
     * falls back to the share, which is the same fact said less plainly.
     */
    const total = focusIsCountry ? (detail?.n_watersheds ?? null) : null;
    const reading = driversReading(drivers, band, focusIsCountry ? { total } : null);
    const landText = reading ? joinClauses(reading.land) : '';
    const rainText = reading?.rain ? say(reading.rain) : '';
    const sentence = !reading
      ? null
      : landText && rainText
        ? t('intel.uc2.drivers.reading', { land: landText, rain: rainText })
        : landText
          ? t('intel.uc2.drivers.readingNoRain', { land: landText })
          : t('intel.uc2.drivers.readingRainOnly', { rain: openWith(rainText) });

    return frame(
      <>
        <ChartFrame
          title={title}
          subtitle={focusName}
          xLabel={t('intel.axis.change')}
          yLabel={t('intel.axis.driver')}
          testId="uc2-drivers-focus-chart"
        >
          {() => (
            <DimensionBars
              rows={bars}
              ariaLabel={`${title}, ${focusName}`}
              refCaption=""
              selectedId={null}
            />
          )}
        </ChartFrame>
        <p className="sub" data-testid="uc2-drivers-caption">
          {caption}
        </p>
        {sentence ? (
          <p id="uc2DriversReading" data-testid="uc2-drivers-reading">
            {sentence}
          </p>
        ) : null}
      </>,
    );
  }

  const barRows: PairedBarsRow[] = measured.map((row) => {
    const d = row.change_drivers!;
    const greening = greeningOf(d, countryLevel);
    return {
      id: row.unit_id,
      label: row.name,
      a: num(d.cropland_change_pct),
      b: num(d.veg_change_pct),
      marker: num(d.rain_change_pct),
      aColor: countryLevel ? shareInk(d.share_cropland_ok_pct) : inkOf(d.cropland_ok),
      bColor: countryLevel ? shareInk(d.share_veg_ok_pct) : inkOf(d.veg_ok),
      // Hatched where there is nothing to judge: a null flag at watershed level, a share
      // the run did not serve at country level.
      aHatched: countryLevel ? num(d.share_cropland_ok_pct) == null : d.cropland_ok == null,
      bHatched: countryLevel ? num(d.share_veg_ok_pct) == null : d.veg_ok == null,
      ...(greening
        ? { c: 1, cText: greening.text, cColor: greening.color, cHatched: greening.hatched }
        : {}),
      // At country level the bars draw a mean, and the readout says in how many of that
      // country's watersheds each check actually passed.
      ...(countryLevel
        ? { note: joinClauses(driversReading(d, band, { total: null })?.land ?? []) }
        : {}),
    };
  });

  const hasGreening = barRows.some((r) => r.cText != null);
  const legend: PairedBarsLegendItem[] = [
    { id: 'farmland', label: t('intel.uc2.drivers.legend.farmland'), color: DRIVER_OK_COLOR },
    {
      id: 'veg',
      label: t(`intel.uc2.drivers.legend.${hasGreening ? 'vegMiddle' : 'veg'}`),
      color: DRIVER_OK_COLOR,
    },
    ...(hasGreening
      ? [
          {
            id: 'greening',
            label: t('intel.uc2.drivers.legend.greening'),
            color: DRIVER_OK_COLOR,
          },
        ]
      : []),
    { id: 'notOk', label: t('intel.uc2.drivers.legend.notOk', { band }), color: DRIVER_NOT_COLOR },
    {
      id: 'unknown',
      label: t('intel.uc2.drivers.legend.unknown'),
      color: DRIVER_NOT_COLOR,
      hatched: true,
    },
    { id: 'rain', label: t('intel.uc2.drivers.legend.rain'), color: 'var(--ink)', marker: true },
    { id: 'band', label: t('intel.uc2.drivers.legend.band', { band }), band: true },
  ];

  return frame(
    <>
      <ChartFrame
        title={title}
        xLabel={t('intel.axis.change')}
        yLabel={t(countryLevel ? 'charts.axis.country' : 'charts.axis.watershed')}
        testId="uc2-drivers-chart"
      >
        {() => (
          <PairedBars
            rows={barRows}
            labels={[
              t('intel.uc2.drivers.legend.farmland'),
              t(`intel.uc2.drivers.legend.${hasGreening ? 'vegMiddle' : 'veg'}`),
              t('intel.uc2.drivers.legend.greening'),
            ]}
            unit="%"
            ariaLabel={title}
            signed
            band={band}
            cNominal
            legend={legend}
            markerLabel={t('intel.uc2.drivers.legend.rain')}
            selectedId={selectedId}
          />
        )}
      </ChartFrame>
      <p className="sub" data-testid="uc2-drivers-caption">
        {caption}
      </p>
    </>,
  );
}

export default Uc2Drivers;
